import { useMemo, useState, useEffect, useCallback } from 'react'
import { usePainterScheduleQuery } from '@paintscout/api'
import type { OptionsDocument, QuoteDocument } from 'paintscout'
import type { PainterScheduleEvent } from '../PainterScheduleView'
import { buildPainterScheduleEvents, normalizeUserId } from './painterScheduleData'
import { getPainterScheduleEvents } from './painterSchedulePageUtils'
import { buildPainterScheduleMockEvents } from './painterScheduleMockData'
import { getCrews } from '@paintscout/util'
import moment from 'moment'

interface UsePainterScheduleDataArgs {
  userId: string | undefined
  currentUserId: string | undefined
  options: OptionsDocument | undefined
  isPainter: boolean
  showMockEvents: boolean
}

function getDateRange() {
  return {
    start: moment().startOf('week').subtract(1, 'week').startOf('day').toISOString(),
    end: moment().startOf('week').add(2, 'weeks').endOf('week').endOf('day').toISOString()
  }
}

export function usePainterScheduleData({
  userId,
  currentUserId,
  options,
  isPainter,
  showMockEvents
}: UsePainterScheduleDataArgs) {
  const { start, end } = useMemo(() => getDateRange(), [])

  const crews = useMemo(() => (options ? getCrews({ options }) : []), [options])
  const userCrewIds = useMemo(() => {
    if (!currentUserId || !crews.length) return new Set<string>()
    const normalized = normalizeUserId(currentUserId)
    return new Set(
      crews.filter((crew) => crew.members?.some((m) => normalizeUserId(m.id) === normalized)).map((crew) => crew._id)
    )
  }, [crews, currentUserId])

  const { data, loading } = usePainterScheduleQuery({
    variables: { startDate: start, endDate: end, skipSanitize: isPainter },
    fetchPolicy: 'cache-and-network',
    skip: !userId
  })

  const deals = useMemo(() => (data?.painterSchedule?.deals ?? []) as any[], [data?.painterSchedule?.deals])

  const [localQuoteOverrides, setLocalQuoteOverrides] = useState<Record<string, QuoteDocument>>({})

  const setLocalQuoteOverride = useCallback((quote: QuoteDocument) => {
    if (!quote?._id) return
    setLocalQuoteOverrides((prev) => ({ ...prev, [quote._id]: quote }))
  }, [])

  const offlineQuotes = useMemo(
    () => ({
      quotes: [],
      invoices: deals.flatMap((d: any) => d.invoices ?? []).map((q: any) => localQuoteOverrides[q._id] ?? q),
      contacts: [],
      templates: []
    }),
    [deals, localQuoteOverrides]
  )

  const activities = useMemo(() => deals.map((d: any) => d.event).filter(Boolean), [deals])

  const dealFilesByDealId = useMemo(
    () =>
      deals.reduce<Record<string, any[]>>((acc, deal) => {
        if (deal.dealId && deal.dealFiles?.length) {
          acc[deal.dealId] = deal.dealFiles
        }
        return acc
      }, {}),
    [deals]
  )

  const dealJobAddressByDealId = useMemo(
    () =>
      deals.reduce<Record<string, any>>((acc, deal) => {
        if (deal.dealId && deal.dealJobAddress) {
          acc[deal.dealId] = deal.dealJobAddress
        }
        return acc
      }, {}),
    [deals]
  )

  const realEvents = useMemo<PainterScheduleEvent[]>(
    () =>
      buildPainterScheduleEvents({
        activities,
        dealFilesByDealId,
        dealJobAddressByDealId,
        offlineQuotes,
        currentUserId,
        userCrewIds,
        options: options as OptionsDocument
      }),
    [activities, dealFilesByDealId, dealJobAddressByDealId, offlineQuotes, options, currentUserId, userCrewIds]
  )

  const mockEvents = useMemo(
    () => (showMockEvents ? buildPainterScheduleMockEvents(new Date(), currentUserId) : []),
    [showMockEvents, currentUserId]
  )
  const initialEvents = useMemo(() => getPainterScheduleEvents({ realEvents, mockEvents }), [realEvents, mockEvents])
  const [eventsState, setEventsState] = useState<PainterScheduleEvent[]>(initialEvents)
  useEffect(() => {
    setEventsState(initialEvents)
  }, [initialEvents])

  return { eventsState, setEventsState, setLocalQuoteOverride, loading }
}
